import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './SeasonEpisodeSelector.css';

/**
 * SeasonEpisodeSelector - Reusable season dropdown component
 * 
 * Extracted from SeriesPDP to be used across watch pages, modals, and other series interfaces.
 * Provides a dropdown for season selection with click-outside-to-close functionality.
 */
const SeasonEpisodeSelector = ({
  seasons = [],
  selectedSeason = null,
  onSeasonSelect,
  className = '',
  variant = 'default',
  placeholder = 'Select Season',
  disabled = false,
  showEpisodeCount = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSeasonSelect = (season) => {
    onSeasonSelect(season);
    setIsOpen(false);
  };

  const getDisplayTitle = (season) => {
    if (season.displayTitle) return season.displayTitle;
    if (season.title) return season.title;
    if (season.name) return season.name;
    return `Season ${season.season_number || '?'}`;
  };

  const getEpisodeCount = (season) => {
    if (!showEpisodeCount) return '';
    const count = season.episode_count || season.episodes?.length || 0;
    return count > 0 ? ` (${count} episodes)` : '';
  };

  const baseClasses = 'season-episode-selector';
  const variantClasses = {
    default: 'season-selector-default',
    compact: 'season-selector-compact',
    watch: 'season-selector-watch',
    modal: 'season-selector-modal'
  };

  const containerClasses = [
    baseClasses,
    variantClasses[variant] || variantClasses.default,
    disabled ? 'season-selector-disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} ref={dropdownRef} {...props}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`season-selector-button ${isOpen ? 'season-selector-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select season"
      >
        <span className="season-selector-text">
          {selectedSeason ? getDisplayTitle(selectedSeason) : placeholder}
          {selectedSeason && getEpisodeCount(selectedSeason)}
        </span>
        <svg
          className={`season-selector-icon ${isOpen ? 'season-selector-icon-open' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="season-selector-dropdown" role="listbox">
          {seasons.length === 0 ? (
            <div className="season-selector-empty">
              No seasons available
            </div>
          ) : (
            seasons.map((season) => (
              <button
                key={season._id || season.id}
                type="button"
                onClick={() => handleSeasonSelect(season)}
                className={`season-selector-option ${
                  selectedSeason && selectedSeason._id === season._id 
                    ? 'season-selector-option-selected' 
                    : ''
                }`}
                role="option"
                aria-selected={selectedSeason && selectedSeason._id === season._id}
              >
                <span className="season-selector-option-title">
                  {getDisplayTitle(season)}
                </span>
                {showEpisodeCount && (
                  <span className="season-selector-option-count">
                    {getEpisodeCount(season)}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

SeasonEpisodeSelector.propTypes = {
  /** Array of season objects */
  seasons: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    displayTitle: PropTypes.string,
    title: PropTypes.string,
    name: PropTypes.string,
    season_number: PropTypes.number,
    episode_count: PropTypes.number,
    episodes: PropTypes.array
  })),
  
  /** Currently selected season object */
  selectedSeason: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    displayTitle: PropTypes.string,
    title: PropTypes.string,
    name: PropTypes.string
  }),
  
  /** Callback function when season is selected */
  onSeasonSelect: PropTypes.func.isRequired,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Visual variant of the selector */
  variant: PropTypes.oneOf(['default', 'compact', 'watch', 'modal']),
  
  /** Placeholder text when no season is selected */
  placeholder: PropTypes.string,
  
  /** Whether the selector is disabled */
  disabled: PropTypes.bool,
  
  /** Whether to show episode count in options */
  showEpisodeCount: PropTypes.bool
};

export default SeasonEpisodeSelector;

